/**
 * Regras de negócio do carreteiro — espelho do lançamento do SIGA web
 * (ver APP-CARRETEIRO-ALINHAMENTO-SIGA.md, seções 2 e 3).
 *
 * ARQUIVO DUPLICADO DE PROPÓSITO — existe idêntico em:
 *   backend/src/carreteiro/regras.ts             (valor oficial + validação no sync)
 *   web/src/features/carreteiro/carreteiroRegras.ts (prévia + travas da tela)
 * O teste backend/test/carreteiroRegras.test.ts falha se os dois divergirem.
 * Por isso este arquivo não tem imports: precisa compilar nos dois projetos.
 */

export type TipoFicha = 'R' | 'U'
export type SN = 'S' | 'N'

/** Campos da viagem que as regras leem. Telas e backend usam tipos maiores. */
export interface ViagemRegra {
  servicoExecutadoId: number | null
  localCargaId: number | null
  localDescargaId: number | null
  cargaHora: string | null
  descargaHora: string | null
  cargaDtFixo: number | string | null
  cargaEstacaOrigem: string | null
  descargaEstacaDescarga: string | null
  cargaChapaApontador: string | null
  descargaChapaApontador: string | null
  descargaBairro: string | null
  descargaRua: string | null
  descargaLargura: number | null
  descargaComprimento: number | null
  descargaPosicao: string | null
  volume: number | string | null
  nf: string | null
  concreto: SN | null
  qdeVg: number | null
}

export interface ServicoRegra {
  nome: string
  inativo: boolean
}

/** Lookups de catálogo. No app vêm dos snapshots da ficha; no backend, do banco. */
export interface CtxRegras {
  servico: (id: number | null) => ServicoRegra | null | undefined
  /** cdt_local_carga.local_carga_origem ('P' = pista, 'U' = usina, ...) */
  localCarga: (id: number | null) => { origem: string | null } | null | undefined
  /** R: ..._rodoviario_deposito ('P' = pista). U: ..._urbana_deposito ('A' = avenida, 'R' = rua) */
  localDescarga: (id: number | null) => { deposito: string | null } | null | undefined
}

export interface ErroViagem {
  campo: CampoViagem
  mensagem: string
}

export type CampoViagem =
  | 'servico'
  | 'volume'
  | 'origem'
  | 'cargaHora'
  | 'descargaHora'
  | 'destino'
  | 'dtFixo'
  | 'estacaOrigem'
  | 'estacaDescarga'

// ============================================================================
//  Utilitários — espelham a conversão de números do PHP
// ============================================================================

export const vazio = (v: unknown): boolean =>
  v === null || v === undefined || String(v).trim() === ''
export const dec = (v: unknown): number =>
  vazio(v) ? 0 : Number(String(v).replace(',', '.')) || 0
/** PHP converte "123+10" em 123; parseInt faz o mesmo. */
export const int = (v: unknown): number => (vazio(v) ? 0 : parseInt(String(v), 10) || 0)
export const arred2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100
export const arred4 = (n: number): number => Math.round((n + Number.EPSILON) * 10000) / 10000

/** Data de hoje (ou de `quando`) no fuso de São Paulo, formato YYYY-MM-DD. */
export function hojeSP(quando: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(quando)
}

/** 'YYYY-MM-DD' → 'DD/MM/AAAA' (formato das mensagens do SIGA). */
export function dataBR(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

// ============================================================================
//  Posição
// ============================================================================

export const POSICOES_R = [
  'LD', 'LE', 'PD-LD', 'PD-LE', '3-FX-LD', '3-FX-LE', 'AC-PD-LD', 'AC-PD-LE', 'AC-LD', 'AC-LE',
] as const
export const POSICOES_U = ['LD', 'LE', 'PD-LD', 'PD-LE'] as const

/** No SIGA a posição só existe com destino pista (R) ou avenida/rua (U). */
export function posicaoPermitida(tipo: TipoFicha, deposito: string | null | undefined): boolean {
  if (!deposito) return false
  return tipo === 'R' ? deposito === 'P' : deposito === 'A' || deposito === 'R'
}

// ============================================================================
//  Normalização (o que o SIGA faz ao gravar)
// ============================================================================

export function normalizarViagem<T extends ViagemRegra>(
  tipo: TipoFicha,
  fichaConcreto: SN,
  v: T,
  ctx: CtxRegras,
): T {
  const dep = ctx.localDescarga(v.localDescargaId)?.deposito
  const out: T = { ...v }
  out.concreto = fichaConcreto
  out.qdeVg = 1
  if (!posicaoPermitida(tipo, dep)) out.descargaPosicao = null
  if (fichaConcreto === 'N') {
    // NF, largura e comprimento só existem em ficha com concreto (NF/ticket)
    out.nf = null
    out.descargaLargura = null
    out.descargaComprimento = null
  }
  if (tipo === 'R') {
    out.descargaBairro = null
    out.descargaRua = null
  } else {
    out.cargaEstacaOrigem = null
    out.descargaEstacaDescarga = null
    // SIGA: apontador de descarga urbano vazio vira '000000'
    if (vazio(out.descargaChapaApontador)) out.descargaChapaApontador = '000000'
  }
  // Chapas: só dígitos, até 6. Outro formato grava null (mesma regra do SIGA).
  if (!vazio(out.cargaChapaApontador) && !/^\d{1,6}$/.test(String(out.cargaChapaApontador))) {
    out.cargaChapaApontador = null
  }
  if (!vazio(out.descargaChapaApontador) && !/^\d{1,6}$/.test(String(out.descargaChapaApontador))) {
    out.descargaChapaApontador = null
  }
  return out
}

// ============================================================================
//  Validação de cada viagem (mensagens iguais às do SIGA)
// ============================================================================

export function validarViagem(tipo: TipoFicha, v: ViagemRegra, ctx: CtxRegras): ErroViagem[] {
  const e: ErroViagem[] = []
  const add = (campo: CampoViagem, mensagem: string) => e.push({ campo, mensagem })
  const s = v.servicoExecutadoId == null ? null : ctx.servico(v.servicoExecutadoId)

  // Serviço
  if (!s) add('servico', 'Preencha o serviço!')
  else if (s.inativo) add('servico', 'Serviço inativo, selecione outro serviço.')

  // Volume: obrigatório com ou sem NF
  if (!(dec(v.volume) > 0)) add('volume', 'Preencha o volume!')

  // Origem, horas, destino
  if (!v.localCargaId) add('origem', tipo === 'U' ? 'Selecione a origem' : 'Preencha o origem!')
  if (vazio(v.cargaHora)) add('cargaHora', tipo === 'U' ? 'Preencha a hora' : 'Preencha a hora de carga!')
  if (vazio(v.descargaHora)) {
    add('descargaHora', tipo === 'U' ? 'Preencha a hora' : 'Preencha a hora de descarga!')
  }
  if (
    !vazio(v.cargaHora) &&
    !vazio(v.descargaHora) &&
    String(v.cargaHora).slice(0, 5) > String(v.descargaHora).slice(0, 5)
  ) {
    add('descargaHora', 'A hora carga deverá ser menor que a hora descarga')
  }
  if (!v.localDescargaId) {
    add('destino', tipo === 'U' ? 'Selecione o Destino / Avenida / Rua' : 'Preencha o destino!')
  }

  // DT fixo: obrigatório no urbano; no rodoviário só quando o serviço é "km"
  const servicoKm = s != null && s.nome.toLowerCase().includes('km')
  if ((tipo === 'U' || servicoKm) && vazio(v.cargaDtFixo)) {
    add('dtFixo', 'Preencha a DT fixo (km)')
  } else if (servicoKm && s != null && !s.nome.includes(String(Math.trunc(dec(v.cargaDtFixo))))) {
    add(
      'dtFixo',
      'O DT fixo (km) incorreto, por favor, inserir o DT fixo correspondente ao serviço executado.',
    )
  }

  // Estacas (só rodoviário). Inclui a regra de edição do web, a mais restritiva.
  if (tipo === 'R') {
    const origem = ctx.localCarga(v.localCargaId)?.origem
    const dep = ctx.localDescarga(v.localDescargaId)?.deposito
    const eo = v.cargaEstacaOrigem
    const ed = v.descargaEstacaDescarga
    if ((origem === 'P' || dep === 'P') && vazio(eo)) add('estacaOrigem', 'Preencha a estaca origem!')
    else if (!vazio(ed) && vazio(eo)) add('estacaOrigem', 'Preencha a estaca origem!')
    if (dep === 'P' && vazio(ed)) add('estacaDescarga', 'Preencha a estaca/descarga!')
    else if (!vazio(eo) && vazio(ed)) add('estacaDescarga', 'Preencha a estaca/descarga!')
    if (!vazio(eo) && int(eo) === 0) add('estacaOrigem', 'A estaca origem não pode ser zerada!')
    if (!vazio(ed) && int(ed) === 0) add('estacaDescarga', 'A estaca/descarga não pode ser zerada!')
    if (!vazio(eo) && !vazio(ed) && int(eo) !== 0 && int(eo) === int(ed)) {
      add('estacaDescarga', 'A estaca origem e a descarga devem ser diferentes!')
    }
  }
  return e
}

/**
 * Índice da primeira viagem cujo `concreto` difere do da ficha (-1 se todas
 * batem). No SIGA web a lista de viagens filtra por concreto = ficha_concreto:
 * viagem divergente some da tela mas continua somando no valor.
 */
export function concretoDivergente(fichaConcreto: SN, viagens: Array<{ concreto?: SN | null }>): number {
  return viagens.findIndex((v) => (v.concreto ?? 'N') !== fichaConcreto)
}

// ============================================================================
//  Cálculo
// ============================================================================

export interface ContratoCalculo {
  garantia: number | null
  preco: number | null
}

export interface ResultadoCalculo {
  dtcalc: number
  dtpg: number
  producao: number
  vpgcar: number
  mtransp: number | null
  qdeVg: 1
}

/**
 * Cálculo do SIGA (classCalculo). Sem qde_vg; DTPg respeita a garantia
 * (distância mínima paga) do item de contrato. Valores sem arredondar —
 * use `arredondarParaGravar` antes de persistir.
 */
export function calcularViagem(tipo: TipoFicha, v: ViagemRegra, contrato: ContratoCalculo): ResultadoCalculo {
  const volume = dec(v.volume)
  const dtf = dec(v.cargaDtFixo)
  // 1 estaca = 20 m → 50 estacas = 1 km. abs: estacas invertidas medem igual.
  const dtcalc =
    tipo === 'R'
      ? Math.abs(int(v.descargaEstacaDescarga) - int(v.cargaEstacaOrigem)) * 20 * 0.001 + dtf
      : dtf
  const garantia = contrato.garantia ?? 0
  const dtpg = dtcalc > garantia ? dtcalc : garantia
  return {
    dtcalc,
    dtpg,
    producao: volume * dtpg,
    vpgcar: volume * dtpg * (contrato.preco ?? 0),
    mtransp: tipo === 'R' ? volume + dtcalc : null,
    qdeVg: 1,
  }
}

/** Escala das colunas: producao/vpgcar decimal(10,2); dtcalc/dtpg/mtransp decimal(13,4). */
export function arredondarParaGravar(tipo: TipoFicha, r: ResultadoCalculo): ResultadoCalculo {
  const casas = tipo === 'U' ? arred2 : arred4
  return {
    dtcalc: casas(r.dtcalc),
    dtpg: casas(r.dtpg),
    producao: arred2(r.producao),
    vpgcar: arred2(r.vpgcar),
    mtransp: r.mtransp == null ? null : arred4(r.mtransp),
    qdeVg: 1,
  }
}

/** O SIGA soma os VPgCar sem arredondar e arredonda só o total do cabeçalho. */
export function valorFicha(viagens: ResultadoCalculo[]): number {
  return arred2(viagens.reduce((t, r) => t + r.vpgcar, 0))
}

// ============================================================================
//  Transferência do caminhão (regra de inserção do SIGA)
// ============================================================================

/**
 * `dt` e `ult.dt` em 'YYYY-MM-DD' — comparação lexicográfica é comparação de
 * data (o SIGA compara texto sem zero à esquerda; esse bug não é copiado).
 */
export function bloqueadoPorTransferencia(
  dt: string,
  ult: { dt: string; destinoId: number } | null,
  chefeSessao: number,
): boolean {
  if (!ult) return false
  const d = dt.slice(0, 10)
  const t = ult.dt.slice(0, 10)
  const receptor = chefeSessao === ult.destinoId
  if (d === t) return !receptor // no dia: só quem recebeu lança
  if (d < t) return receptor // antes: quem recebeu não lança
  return !receptor // depois: quem transferiu não lança
}

// ============================================================================
//  Sync — reenviar ou recusar
// ============================================================================

/**
 * Status HTTP de uma tentativa de envio → o que fazer com a ficha.
 * 0 = sem rede. 401 = sessão expirou (reenvia depois do login).
 */
export function classificarFalhaSync(status: number): 'REENVIAR' | 'REJEITADA' {
  if (status === 0 || status === 401 || status === 408 || status === 429 || status >= 500) {
    return 'REENVIAR'
  }
  return status >= 400 ? 'REJEITADA' : 'REENVIAR'
}

/** Recusas que o operador não resolve editando a ficha — só resta descartar. */
export const RECUSAS_SEM_CORRECAO = [
  'MEDICAO_GERADA',
  'BLOQUEIO_TRANSFERENCIA',
  'PRAZO_RETROATIVO',
  'MVE_SEM_CONTRATO_OU_PERMISSAO',
  'FORA_VIGENCIA_CONTRATO',
  'CHEFE_EQUIPE_INVALIDO',
  'OBRA_NAO_ENCONTRADA',
  'PROJETO_NAO_AUTORIZADO',
] as const
